import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Download, Upload, FileText, TrendingUp, TrendingDown, DollarSign } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = ["Maintenance", "Fuel", "Insurance", "Registration", "Cleaning", "Parts", "Tools", "Towing", "Office", "Marketing", "Other"];

function money(v) { return `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`; }

export default function TaxPnL({ token, onAuthError }) {
  const [pnl, setPnl] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [year, setYear] = useState("");
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), category: "Maintenance", amount: "", vendor: "", vehicle_id: "", notes: "" });
  const [receipt, setReceipt] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = async () => {
    setLoading(true);
    try {
      const [p, e, v] = await Promise.all([
        axios.get(`${API}/pnl${year ? `?year=${year}` : ""}`, auth),
        axios.get(`${API}/expenses${year ? `?year=${year}` : ""}`, auth),
        axios.get(`${API}/vehicles`),
      ]);
      setPnl(p.data); setExpenses(e.data || []); setVehicles(v.data || []);
    } catch (err) {
      if (err?.response?.status === 401 && onAuthError) onAuthError();
      else toast.error("Failed to load P&L");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year]);

  const onReceipt = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/expenses/upload-receipt`, fd, { headers: { ...auth.headers, "Content-Type": "multipart/form-data" } });
      setReceipt(data);
      toast.success("Receipt attached");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); e.target.value = ""; }
  };

  const addExpense = async () => {
    if (!form.amount || Number(form.amount) <= 0) { toast.error("Enter an amount greater than $0"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/expenses`, {
        ...form, amount: Number(form.amount),
        receipt_path: receipt?.path, receipt_content_type: receipt?.content_type, receipt_filename: receipt?.filename,
      }, auth);
      toast.success("Expense added");
      setForm({ date: new Date().toISOString().slice(0, 10), category: "Maintenance", amount: "", vendor: "", vehicle_id: "", notes: "" });
      setReceipt(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save expense");
    } finally { setSaving(false); }
  };

  const del = async (id) => {
    const reason = window.prompt("Void this expense? Enter a reason (kept in the audit log):");
    if (!reason || !reason.trim()) return;
    try { await axios.post(`${API}/expenses/${id}/void`, { reason }, auth); toast.success("Expense voided"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not void"); }
  };

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/pnl/export${year ? `?year=${year}` : ""}`, { ...auth, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `pnl-${year || "all"}.csv`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  if (loading && !pnl) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#F2B705]" /></div>;
  const t = pnl?.totals || { income: 0, expense: 0, net: 0 };

  return (
    <div className="space-y-6" data-testid="tax-pnl">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-white">Profit &amp; Loss / Tax</h2>
          <p className="text-white/50 text-sm">Income auto-pulled from bookings · log expenses with receipts · export for taxes.</p>
        </div>
        <div className="flex items-center gap-2">
          <select data-testid="pnl-year-select" value={year} onChange={(e) => setYear(e.target.value)}
            className="px-3 py-2 rounded-md bg-[#0C0C0C] border border-white/15 text-white text-sm outline-none">
            <option value="">All Years</option>
            {(pnl?.years || []).map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <button data-testid="pnl-export-btn" onClick={exportCsv} className="flex items-center gap-1.5 px-3 py-2 rounded-md bg-[#0C0C0C] border border-white/10 text-white/80 text-sm font-semibold hover:text-white">
            <Download size={15} /> Export CSV
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="rounded-xl border border-green-500/30 bg-[#0C0C0C] p-4" data-testid="pnl-income">
          <div className="flex items-center gap-2 text-white/50 text-xs uppercase tracking-widest"><TrendingUp size={15} /> Income</div>
          <div className="font-display font-black text-3xl mt-2 text-green-400">{money(t.income)}</div>
        </div>
        <div className="rounded-xl border border-red-500/30 bg-[#0C0C0C] p-4" data-testid="pnl-expense">
          <div className="flex items-center gap-2 text-white/50 text-xs uppercase tracking-widest"><TrendingDown size={15} /> Expenses</div>
          <div className="font-display font-black text-3xl mt-2 text-red-400">{money(t.expense)}</div>
        </div>
        <div className={`rounded-xl border bg-[#0C0C0C] p-4 ${t.net >= 0 ? "border-[#F2B705]/40" : "border-red-500/40"}`} data-testid="pnl-net">
          <div className="flex items-center gap-2 text-white/50 text-xs uppercase tracking-widest"><DollarSign size={15} /> Net Profit</div>
          <div className={`font-display font-black text-3xl mt-2 ${t.net >= 0 ? "text-[#F2B705]" : "text-red-400"}`}>{money(t.net)}</div>
        </div>
      </div>

      {/* Add expense */}
      <div className="rounded-xl bg-[#0C0C0C] border border-white/10 p-5">
        <h3 className="text-white font-semibold mb-3 flex items-center gap-2"><Plus size={16} /> Add Expense</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <input data-testid="expense-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="px-3 py-2 rounded-md bg-black border border-white/15 text-white text-sm outline-none" />
          <select data-testid="expense-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="px-3 py-2 rounded-md bg-black border border-white/15 text-white text-sm outline-none">
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <input data-testid="expense-amount" type="number" step="0.01" placeholder="Amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="px-3 py-2 rounded-md bg-black border border-white/15 text-white text-sm outline-none" />
          <input data-testid="expense-vendor" placeholder="Vendor" value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} className="px-3 py-2 rounded-md bg-black border border-white/15 text-white text-sm outline-none" />
          <select data-testid="expense-vehicle" value={form.vehicle_id} onChange={(e) => setForm({ ...form, vehicle_id: e.target.value })} className="px-3 py-2 rounded-md bg-black border border-white/15 text-white text-sm outline-none">
            <option value="">No car</option>
            {vehicles.map((v) => <option key={v.id} value={v.id}>{v.year} {v.make} {v.model}</option>)}
          </select>
          <input data-testid="expense-notes" placeholder="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="px-3 py-2 rounded-md bg-black border border-white/15 text-white text-sm outline-none" />
        </div>
        <div className="flex flex-wrap items-center gap-3 mt-3">
          <label data-testid="expense-receipt-upload" className="inline-flex items-center gap-2 cursor-pointer border border-white/15 hover:border-[#F2B705]/50 rounded-md px-3 py-2 text-sm text-white/60 transition-colors">
            {uploading ? <Loader2 className="animate-spin" size={15} /> : <Upload size={15} />}
            {receipt ? "Receipt attached ✓" : "Attach receipt (image/PDF)"}
            <input type="file" accept="image/*,application/pdf" className="hidden" onChange={onReceipt} />
          </label>
          <button data-testid="expense-save-btn" onClick={addExpense} disabled={saving} className="flex items-center gap-1.5 px-4 py-2 rounded-md bg-[#F2B705] text-black text-sm font-bold disabled:opacity-60">
            {saving ? <Loader2 className="animate-spin" size={15} /> : <Plus size={15} />} Add Expense
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Category + per-vehicle breakdown */}
        <div className="space-y-6">
          <div className="rounded-xl bg-[#0C0C0C] border border-white/10 p-5">
            <h3 className="text-white font-semibold mb-3">Expenses by Category</h3>
            {(pnl?.categories || []).length === 0 ? <p className="text-white/35 text-sm">No expenses yet.</p> : (
              <div className="space-y-2">{pnl.categories.map((c) => (
                <div key={c.category} className="flex items-center justify-between text-sm"><span className="text-white/70">{c.category}</span><span className="text-red-400 font-semibold">{money(c.amount)}</span></div>
              ))}</div>
            )}
          </div>
          <div className="rounded-xl bg-[#0C0C0C] border border-white/10 p-5">
            <h3 className="text-white font-semibold mb-3">Per-Vehicle P&amp;L</h3>
            {(pnl?.per_vehicle || []).length === 0 ? <p className="text-white/35 text-sm">No per-vehicle data yet.</p> : (
              <div className="space-y-2">{pnl.per_vehicle.map((v) => (
                <div key={v.vehicle_id} className="flex items-center justify-between text-sm">
                  <span className="text-white/70 truncate">{v.vehicle}</span>
                  <span className={`font-semibold ${v.net >= 0 ? "text-[#F2B705]" : "text-red-400"}`}>{money(v.net)}</span>
                </div>
              ))}</div>
            )}
          </div>
        </div>

        {/* Monthly */}
        <div className="rounded-xl bg-[#0C0C0C] border border-white/10 p-5">
          <h3 className="text-white font-semibold mb-3">Monthly Breakdown</h3>
          {(pnl?.monthly || []).length === 0 ? <p className="text-white/35 text-sm">No activity yet.</p> : (
            <div className="space-y-1.5">
              <div className="grid grid-cols-4 text-[11px] uppercase tracking-wider text-white/35 pb-1 border-b border-white/10"><span>Month</span><span className="text-right">Income</span><span className="text-right">Expense</span><span className="text-right">Net</span></div>
              {pnl.monthly.map((m) => (
                <div key={m.month} className="grid grid-cols-4 text-sm py-1">
                  <span className="text-white/70">{m.month}</span>
                  <span className="text-right text-green-400">{money(m.income)}</span>
                  <span className="text-right text-red-400">{money(m.expense)}</span>
                  <span className={`text-right font-semibold ${m.net >= 0 ? "text-[#F2B705]" : "text-red-400"}`}>{money(m.net)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Expense list */}
      <div className="rounded-xl bg-[#0C0C0C] border border-white/10 p-5">
        <h3 className="text-white font-semibold mb-3">Logged Expenses ({expenses.length})</h3>
        {expenses.length === 0 ? <p className="text-white/35 text-sm">No expenses logged.</p> : (
          <div className="divide-y divide-white/5" data-testid="expense-list">
            {expenses.map((e) => (
              <div key={e.id} className={`flex items-center justify-between py-2.5 text-sm gap-3 ${e.status === "voided" ? "opacity-45" : ""}`} data-testid={`expense-row-${e.id}`}>
                <div className="min-w-0">
                  <div className="text-white/85 truncate">{e.category} <span className="text-white/40">· {e.vendor || "—"}</span>
                    {e.status === "voided" && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-red-500/20 text-red-400 font-bold">VOIDED</span>}
                  </div>
                  <div className="text-white/40 text-xs">{e.date}{e.notes ? ` · ${e.notes}` : ""}</div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  {e.receipt_path && (
                    <a href={`${API}/files/${e.receipt_path}?auth=${token}`} target="_blank" rel="noreferrer" className="text-[#F2B705] hover:underline flex items-center gap-1 text-xs"><FileText size={13} /> Receipt</a>
                  )}
                  <span className="text-red-400 font-semibold">{money(e.amount)}</span>
                  {e.status !== "voided" && <button data-testid={`expense-delete-${e.id}`} onClick={() => del(e.id)} className="text-white/40 hover:text-red-400"><Trash2 size={15} /></button>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
